import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { clientsApi } from '../api/clients'
import { trendsApi, type SeriesPoint } from '../api/trends'
import { LineChartSvg, type ChartLine } from '../components/LineChartSvg'
import { KpiMultiAxisChart, type KpiAxisSeries } from '../components/KpiMultiAxisChart'
import { formatDateTimeBR } from '../lib/format'
import { ApiError } from '../lib/api'
import { Spinner } from '../components/Spinner'
import { ExportPdfButton } from '../components/ExportPdfButton'

const CUT_META: Record<string, { label: string; color: string }> = {
  last_7d: { label: '7 dias', color: '#10b981' },
  last_15d: { label: '15 dias', color: '#0ea5e9' },
  last_30d: { label: '30 dias', color: '#8b5cf6' },
  last_60d: { label: '60 dias', color: '#f59e0b' },
}
const CUT_ORDER = ['last_7d', 'last_15d', 'last_30d', 'last_60d']

const METRIC_LABEL: Record<string, string> = {
  spend: 'Investimento',
  revenue: 'Receita',
  conversions: 'Conversões',
  roas: 'ROAS',
  cost_per_conversion: 'CPA (custo/conversão)',
  impressions: 'Impressões',
  clicks: 'Cliques',
  ctr: 'CTR (%)',
  cpc: 'CPC',
  cpm: 'CPM',
  reach: 'Alcance',
  frequency: 'Frequência',
  post_engagement: 'Engajamento',
  cost_per_engagement: 'Custo/Engajamento',
}
const METRIC_PRIORITY = ['roas', 'cost_per_conversion', 'spend', 'conversions', 'revenue']
const CONSOLIDATED = 'Consolidado'

// KPIs do gráfico multi-eixo (ordem = disposição: 2 à esquerda, 2 à direita).
// Cada um usa sua própria escala Y; se a métrica não existir p/ o cliente, é ignorada.
const KPI_CHART_CONFIG: Array<{
  key: string
  short: string
  color: string
  side: 'left' | 'right'
  decimals: number
  prefix?: string
}> = [
  { key: 'roas', short: 'ROAS', color: '#0ea5e9', side: 'left', decimals: 2 },
  { key: 'cost_per_conversion', short: 'CPA', color: '#8b5cf6', side: 'left', decimals: 0, prefix: 'R$ ' },
  { key: 'cpc', short: 'CPC', color: '#f59e0b', side: 'right', decimals: 2, prefix: 'R$ ' },
  { key: 'conversions', short: 'Conv.', color: '#10b981', side: 'right', decimals: 0 },
]

// Janela única do gráfico de KPIs (padrão: 15 dias).
const KPI_WINDOWS = [
  { cut: 'last_7d', label: '7 dias' },
  { cut: 'last_15d', label: '15 dias' },
  { cut: 'last_30d', label: '30 dias' },
  { cut: 'last_60d', label: '60 dias' },
]

const kpiFmt = (decimals: number, prefix?: string) => (n: number) =>
  (prefix ?? '') +
  n.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })

function metricValue(p: SeriesPoint, metric: string, platform: string): number | null {
  if (platform === CONSOLIDATED) {
    const v = p.kpis.consolidado?.[metric]
    return typeof v === 'number' ? v : null
  }
  const row = (p.kpis.porPlataforma ?? []).find((r) => r.plataforma === platform)
  const v = row?.[metric]
  return typeof v === 'number' ? v : null
}

export default function Trends() {
  const { data: clientsData } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list() })
  const clients = useMemo(
    () => (clientsData?.clients ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [clientsData]
  )

  const [clientId, setClientId] = useState('')
  useEffect(() => {
    if (!clientId && clients.length) setClientId(clients[0].id)
  }, [clients, clientId])

  const { data, isLoading } = useQuery({
    queryKey: ['kpi-series', clientId],
    queryFn: () => trendsApi.series(clientId),
    enabled: !!clientId,
  })

  // Metas do cliente (linhas tracejadas no gráfico de KPIs).
  const { data: clientDetail } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.get(clientId),
    enabled: !!clientId,
  })
  const targetMap = useMemo(() => {
    const m: Record<string, number> = {}
    for (const t of clientDetail?.client.kpiTargets ?? []) m[t.metric] = t.value
    return m
  }, [clientDetail])

  const qc = useQueryClient()
  const { data: analysisData } = useQuery({
    queryKey: ['trend-analysis', clientId],
    queryFn: () => trendsApi.analysis(clientId),
    enabled: !!clientId,
  })
  const analyze = useMutation({
    mutationFn: () => trendsApi.analyze(clientId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trend-analysis', clientId] }),
  })
  const analysis = analysisData?.analysis ?? null
  const analysisRef = useRef<HTMLDivElement>(null)
  const clientName = clients.find((c) => c.id === clientId)?.name ?? ''

  const [selectedCuts, setSelectedCuts] = useState<Set<string>>(new Set(['last_30d']))
  const [metric, setMetric] = useState('')
  const [platform, setPlatform] = useState(CONSOLIDATED)

  // Define a métrica padrão quando os dados carregam.
  useEffect(() => {
    if (!data) return
    if (metric && data.metrics.includes(metric)) return
    const def = METRIC_PRIORITY.find((m) => data.metrics.includes(m)) ?? data.metrics[0] ?? ''
    setMetric(def)
  }, [data, metric])

  const toggleCut = (cut: string) =>
    setSelectedCuts((prev) => {
      const next = new Set(prev)
      if (next.has(cut)) next.delete(cut)
      else next.add(cut)
      return next.size === 0 ? prev : next // mantém ao menos 1
    })

  // Monta as linhas do gráfico.
  const { lines, xLabels } = useMemo(() => {
    if (!data || !metric) return { lines: [] as ChartLine[], xLabels: [] as string[] }
    const cuts = CUT_ORDER.filter((c) => selectedCuts.has(c) && data.series[c]?.length)
    const labelSet = new Set<string>()
    for (const c of cuts) for (const p of data.series[c]) labelSet.add(p.periodEnd)
    const xLabels = [...labelSet].sort()

    const lines: ChartLine[] = cuts.map((c) => {
      const byDate = new Map(data.series[c].map((p) => [p.periodEnd, p]))
      return {
        label: CUT_META[c].label,
        color: CUT_META[c].color,
        data: xLabels.map((d) => {
          const p = byDate.get(d)
          return { x: d, y: p ? metricValue(p, metric, platform) : null }
        }),
      }
    })
    return { lines, xLabels }
  }, [data, metric, platform, selectedCuts])

  // Gráfico de KPIs (multi-eixo): uma janela por vez, KPIs consolidados do cliente.
  const [kpiWindow, setKpiWindow] = useState('last_15d')
  const { kpiSeries, kpiXLabels } = useMemo(() => {
    const empty = { kpiSeries: [] as KpiAxisSeries[], kpiXLabels: [] as string[] }
    if (!data) return empty
    const points = data.series[kpiWindow] ?? []
    if (!points.length) return empty
    const xLabels = points.map((p) => p.periodEnd) // já vem ordenado (asc) do backend
    const kpiSeries = KPI_CHART_CONFIG.flatMap((cfg) => {
      if (!data.metrics.includes(cfg.key)) return []
      const dpts = points.map((p) => ({ x: p.periodEnd, y: metricValue(p, cfg.key, CONSOLIDATED) }))
      if (!dpts.some((d) => d.y != null)) return [] // sem valor na janela → ignora
      return [
        {
          key: cfg.key,
          label: METRIC_LABEL[cfg.key] ?? cfg.key,
          short: cfg.short,
          color: cfg.color,
          side: cfg.side,
          fmtAxis: kpiFmt(cfg.decimals),
          fmtFull: kpiFmt(cfg.decimals, cfg.prefix),
          target: targetMap[cfg.key],
          data: dpts,
        } satisfies KpiAxisSeries,
      ]
    })
    return { kpiSeries, kpiXLabels: xLabels }
  }, [data, kpiWindow, targetMap])

  const platforms = [CONSOLIDATED, ...(data?.platforms ?? [])]

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-slate-900">Tendências</h1>

      {/* Cliente — compartilhado pelos dois gráficos */}
      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4">
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-500">Cliente</label>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Gráfico de KPIs do cliente — múltiplas escalas Y, uma janela por vez */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">KPIs do cliente</h2>
            <p className="text-xs text-slate-400">
              CPA, ROAS, CPC e Conversões — cada um na sua própria escala.
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-slate-300 p-0.5">
            {KPI_WINDOWS.map((w) => (
              <button
                key={w.cut}
                type="button"
                onClick={() => setKpiWindow(w.cut)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  kpiWindow === w.cut ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        {isLoading ? <Spinner /> : <KpiMultiAxisChart series={kpiSeries} xLabels={kpiXLabels} />}
      </div>

      {/* Gráfico principal + seus filtros (métrica/plataforma/cortes) no mesmo card */}
      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Métrica</label>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              {(data?.metrics ?? []).map((m) => (
                <option key={m} value={m}>
                  {METRIC_LABEL[m] ?? m}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Plataforma</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              {platforms.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Cortes</label>
            <div className="flex flex-wrap gap-2">
              {CUT_ORDER.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCut(c)}
                  className={`rounded-md border px-2.5 py-1.5 text-xs font-medium ${
                    selectedCuts.has(c)
                      ? 'border-transparent text-white'
                      : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  style={selectedCuts.has(c) ? { backgroundColor: CUT_META[c].color } : undefined}
                >
                  {CUT_META[c].label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <LineChartSvg lines={lines} xLabels={xLabels} />
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {lines.map((l) => (
                <span key={l.label} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: l.color }} />
                  {l.label}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Análise de tendências (IA, sob demanda) */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Análise de tendências (IA)</h2>
            {analysis && (
              <p className="text-xs text-slate-400">
                Gerada em {formatDateTimeBR(analysis.generatedAt)} · {analysis.periodLabel} ·{' '}
                {analysis.reportsCount} relatórios · {analysis.model}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {analysis && !analyze.isPending && (
              <ExportPdfButton
                targetRef={analysisRef}
                title={`Análise de tendências${clientName ? ` — ${clientName}` : ''}`}
                subtitle={`${analysis.periodLabel} · ${analysis.reportsCount} relatórios · ${
                  analysis.model
                } · gerada em ${formatDateTimeBR(analysis.generatedAt)}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              />
            )}
            <button
              onClick={() => analyze.mutate()}
              disabled={analyze.isPending || !clientId}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {analyze.isPending && <Spinner className="h-4 w-4 text-white" />}
              {analysis ? 'Analisar novamente' : 'Analisar Tendências'}
            </button>
          </div>
        </div>

        {analyze.isPending && (
          <p className="text-sm text-slate-500">
            Analisando os últimos relatórios e as ações concluídas… pode levar até ~30s.
          </p>
        )}
        {analyze.isError && (
          <p className="text-sm text-red-600">
            {analyze.error instanceof ApiError ? analyze.error.message : 'Não foi possível analisar.'}
          </p>
        )}
        {!analyze.isPending && analysis && (
          <div ref={analysisRef} className="markdown border-t border-slate-100 pt-3">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{analysis.content}</ReactMarkdown>
          </div>
        )}
        {!analyze.isPending && !analysis && (
          <p className="text-sm text-slate-500">
            Nenhuma análise ainda. Clique em "Analisar Tendências" para gerar.
          </p>
        )}
      </section>
    </div>
  )
}
